"use client";

import { useEffect } from "react";
import { CONSENT_CHANGE_EVENT, readChoice, type ConsentChoice } from "@/lib/consent";
import { SITE } from "@/site";

// Microsoft Clarity project ID (clarity.microsoft.com → Settings → Overview)
const CLARITY_ID = SITE.clarityId ?? "";
const SCRIPT_SRC = `https://www.clarity.ms/tag/${CLARITY_ID}`;
const CLARITY_COOKIE = /^_cl(ck|sk)$/;

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };
type ClarityWindow = Window & { clarity?: ClarityFn };

function loadClarity() {
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;
  const w = window as ClarityWindow;
  // Same command queue the official snippet sets up; the tag drains it once loaded
  const clarity: ClarityFn = w.clarity ?? ((...args: unknown[]) => { (clarity.q = clarity.q ?? []).push(args); });
  w.clarity = clarity;
  // The script only loads after consent, so tell Clarity analytics storage is granted
  clarity("consentv2", { ad_Storage: "denied", analytics_Storage: "granted" });

  const script = document.createElement("script");
  script.src = SCRIPT_SRC;
  script.async = true;
  document.head.appendChild(script);
}

// Expires Clarity's first-party cookies on the host and every parent domain
function clearClarityCookies() {
  const parts = window.location.hostname.split(".");
  const domains = ["", ...parts.slice(0, -1).map((_, i) => `;domain=.${parts.slice(i).join(".")}`)];
  for (const pair of document.cookie.split(";")) {
    const name = pair.split("=")[0].trim();
    if (!CLARITY_COOKIE.test(name)) continue;
    for (const domain of domains) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domain}`;
    }
  }
}

// Clarity records clicks, scrolls and page interactions and sets cookies, so it shares the
// Analytics consent with GA4 and Ahrefs: without consent the script is never requested.
// A withdrawn consent stops it, deletes its cookies and reloads the page.
export function ClarityAnalytics() {
  useEffect(() => {
    if (!CLARITY_ID) return;
    if (readChoice()?.a === 1) loadClarity();
    else clearClarityCookies();

    function onConsentChange(event: Event) {
      const granted = (event as CustomEvent<ConsentChoice>).detail?.a === 1;
      if (granted) {
        loadClarity();
        return;
      }
      clearClarityCookies();
      if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
        (window as ClarityWindow).clarity?.("consentv2", { ad_Storage: "denied", analytics_Storage: "denied" });
        window.location.reload();
      }
    }

    window.addEventListener(CONSENT_CHANGE_EVENT, onConsentChange);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onConsentChange);
  }, []);

  return null;
}
